import { useState } from 'react';
import { 
  Award, BookOpen, Briefcase, GraduationCap, Trophy, 
  Lightbulb, Users, Globe, Code, Palette, Music, 
  Heart, Target, Handshake, Zap, MessageSquare
} from 'lucide-react';
import Card from '@components/common/Card';

const ScoreCard = () => {
  const milestones = [
    {
      id: 1,
      icon: BookOpen,
      title: 'Cracked a Competitive Exam',
      description: 'Cleared any government or private competitive examination',
      completed: false
    },
    {
      id: 2,
      icon: Code,
      title: 'Completed a Practical Project',
      description: 'Built a real-world project showcasing your skills',
      completed: false
    },
    {
      id: 3,
      icon: GraduationCap,
      title: 'Acquired 9+ CGPA in College',
      description: 'Maintained outstanding academic performance with 9+ CGPA',
      completed: false
    },
    {
      id: 4,
      icon: MessageSquare,
      title: 'Improved Communication Skills',
      description: 'Took a course or training to improve speaking/writing',
      completed: false
    },
    {
      id: 5,
      icon: Trophy,
      title: 'Played State Level Sports',
      description: 'Represented at state level in any sport',
      completed: false
    },
    {
      id: 6,
      icon: Music,
      title: 'Won a Dance Competition',
      description: 'Won or placed in a dance competition',
      completed: false
    },
    {
      id: 7,
      icon: Lightbulb,
      title: 'Developed Aptitude',
      description: 'Practiced and improved logical/quantitative reasoning',
      completed: false
    },
    {
      id: 8,
      icon: Heart,
      title: 'Volunteered for Social Cause',
      description: 'Contributed to community service or NGO work',
      completed: false
    },
    {
      id: 9,
      icon: Code,
      title: 'Participated in a Hackathon',
      description: 'Competed in a coding or innovation hackathon',
      completed: false
    },
    {
      id: 10,
      icon: Briefcase,
      title: 'Got My First Job',
      description: 'Secured your first full-time or part-time job',
      completed: false
    },
    {
      id: 11,
      icon: Briefcase,
      title: 'Completed an Internship',
      description: 'Finished an internship at a company or organization',
      completed: false
    },
    {
      id: 12,
      icon: Award,
      title: 'Earned a Professional Certification',
      description: 'Received a professional or industry certification',
      completed: false
    },
    {
      id: 13,
      icon: Target,
      title: 'Acquired a New Skill',
      description: 'Learned a new technical or professional skill',
      completed: false
    },
    {
      id: 14,
      icon: Trophy,
      title: 'Won a Singing Competition',
      description: 'Won or placed in a singing/music competition',
      completed: false
    },
    {
      id: 15,
      icon: Palette,
      title: 'Won an Art/Creative Competition',
      description: 'Won or placed in painting, photography, or creative arts',
      completed: false
    },
    {
      id: 16,
      icon: Users,
      title: 'Attended a Workshop/Seminar',
      description: 'Participated in an industry workshop or seminar',
      completed: false
    },
    {
      id: 17,
      icon: Handshake,
      title: 'Held a Leadership Role',
      description: 'Led a team, club, or student body',
      completed: false
    },
    {
      id: 18,
      icon: Globe,
      title: 'International Exposure',
      description: 'Attended international conference, exchange program, or collaboration',
      completed: false
    }
  ];

  const [completedMilestones, setCompletedMilestones] = useState(
    milestones.map(m => m.completed)
  );

  const toggleMilestone = (index) => {
    const newCompleted = [...completedMilestones];
    newCompleted[index] = !newCompleted[index];
    setCompletedMilestones(newCompleted);
  };

  const completedCount = completedMilestones.filter(Boolean).length;
  const totalMilestones = milestones.length;
  const scorePercentage = Math.round((completedCount / totalMilestones) * 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (scorePercentage / 100) * circumference;

  const getScoreGrade = () => {
    if (scorePercentage >= 90) return { grade: 'A+', label: 'Excellent', color: 'text-green-600' };
    if (scorePercentage >= 75) return { grade: 'A', label: 'Outstanding', color: 'text-green-500' };
    if (scorePercentage >= 60) return { grade: 'B', label: 'Proficient', color: 'text-blue-600' };
    if (scorePercentage >= 40) return { grade: 'C', label: 'Developing', color: 'text-yellow-600' };
    if (scorePercentage >= 20) return { grade: 'D', label: 'Developing', color: 'text-orange-500' };
    return { grade: 'D', label: 'Developing', color: 'text-orange-500' };
  };

  const scoreGrade = getScoreGrade();

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          <div className="flex flex-col items-center">
            <div className="relative w-32 h-32">
              <svg className="transform -rotate-90 w-32 h-32">
                <circle
                  cx="64"
                  cy="64"
                  r="45"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="45"
                  stroke="#e7474b"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  className="transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-3xl font-bold text-primary-700">{scorePercentage}</div>
                <div className="text-xs text-gray-500">/ 100</div>
              </div>
            </div>
            <div className={`mt-3 px-3 py-1 rounded-full text-xs font-semibold ${scoreGrade.color} bg-gray-100`}>
              {scoreGrade.grade}
            </div>
            <div className="mt-1 text-xs text-gray-500">{scoreGrade.label}</div>
          </div>

          <div className="flex-1">
            <h2 className="text-xl font-bold text-primary-700 mb-2">
              Your Career Clarity Score reflects your readiness based on real accomplishments
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              Select your milestones below to build your score to 100.
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-secondary-500 h-2 rounded-full transition-all duration-500" 
                style={{ width: `${scorePercentage}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {completedCount} of {totalMilestones} milestones achieved
            </p>
          </div>
        </div>
      </div>

      <Card>
        <h3 className="text-lg font-bold text-primary-700 mb-4">
          Career Milestones
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Track your achievements and build your career score
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {milestones.map((milestone, index) => {
            const Icon = milestone.icon;
            const isCompleted = completedMilestones[index];
            
            return (
              <div
                key={milestone.id}
                onClick={() => toggleMilestone(index)}
                className={`
                  flex items-start gap-3 p-4 rounded-xl border cursor-pointer
                  transition-all duration-300 group
                  ${isCompleted 
                    ? 'border-[#c33c3d] bg-gradient-to-br from-[#fee2e2] to-[#fecaca] shadow-md hover:shadow-lg' 
                    : 'border-gray-200 bg-white hover:border-[#c33c3d]/30 hover:shadow-md hover:scale-[1.02]'
                  }
                `}
              >
                <div className={`
                  flex-shrink-0 w-6 h-6 rounded-full border-2 mt-0.5
                  flex items-center justify-center transition-all
                  ${isCompleted 
                    ? 'border-[#c33c3d] bg-[#c33c3d] shadow-sm' 
                    : 'border-gray-300 bg-white group-hover:border-[#c33c3d]/50'
                  }
                `}>
                  {isCompleted && (
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                      isCompleted 
                        ? 'bg-white shadow-sm' 
                        : 'bg-gray-50 group-hover:bg-[#fee2e2]'
                    }`}>
                      <Icon className={`w-5 h-5 transition-colors ${isCompleted ? 'text-[#c33c3d]' : 'text-gray-400 group-hover:text-[#c33c3d]'}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className={`text-sm font-bold transition-colors ${isCompleted ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'}`}>
                        {milestone.title}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default ScoreCard;
